import { useState, useEffect, useRef } from 'react';
import { Image, Form, Row, Col, Button, Card, Overlay, Tooltip } from 'react-bootstrap';
import ProfileMobileSectionActions from './ProfileMobileSectionActions';
import icValidationError from './assets/img/ic-danger.svg';
import { validateField, validateEmail } from './utils/validation';
import icCalendar from './assets/img/ic-calendar.svg';
import icPhone from './assets/img/ic-smart-phone.svg';
import icMail from './assets/img/ic-mail.svg';
import icCompany from './assets/img/ic-building-company.svg';
import icCopy from './assets/img/ic-copy.svg';


const UserAddAbout = ({
    formData,
    handleChange,
    handleReset,
    errors,
    isEditPage,
    handleAboutSubmit,
    companies = [],
    createdDate = [],
    hideEdit,
    disableEmail = false,
    hideCompanyFields = false,
    userRole = '',
}) => {

    useEffect(() => {
        const id = normalizeId(formData?.company);
        if (id && formData?.company !== id) {
            handleChange({ target: { name: 'company', value: id } });
        }
        // If it's empty, parent didn't supply a company — see parent fixes below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData?.company]);

    useEffect(() => {
        return () => {
            if (copyTimeoutRef.current) {
                clearTimeout(copyTimeoutRef.current);
            }
        };
    }, []);

    const normalizeId = (x) => {
        if (!x) return '';
        if (typeof x === 'string') return x;

        if (typeof x === 'object') {
            // Try the most common Mongo/driver encodings
            if (x._id) return normalizeId(x._id);
            if (x.$oid) return String(x.$oid);
            if (x.$id) return normalizeId(x.$id);
            if (x.oid) return String(x.oid);
            if (x.id) return normalizeId(x.id);

            // Driver helpers
            if (typeof x.toHexString === 'function') return x.toHexString();

            // Last resort: trustworthy toString (e.g., Mongoose ObjectId)
            if (typeof x.toString === 'function' && x.toString !== Object.prototype.toString) {
                const s = x.toString();
                if (/^[a-fA-F0-9]{24}$/.test(s)) return s;
            }
        }

        return '';
    };

    const companyId = normalizeId(formData?.company);
    const companyTitle =
        (Array.isArray(companies) ? companies : [])
            .find(c => normalizeId(c._id ?? c.id ?? c) === companyId)
            ?.title
        ?? formData?.company?.title
        ?? '';

    const normalizeDateForInput = (value) => {
        if (!value) return '';

        if (typeof value === 'string') {
            if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
            const parsed = new Date(value);
            return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
        }

        if (typeof value === 'object') {
            if (value.$date) {
                const parsed = new Date(value.$date);
                return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
            }
        }

        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
    };

    const formatDateForDisplay = (value) => {
        const normalized = normalizeDateForInput(value);
        if (!normalized) return 'N/A';

        const [year, month, day] = normalized.split('-');
        return `${day}.${month}.${year}.`;
    };

    const formatDateForUsDisplay = (value) => {
        const normalized = normalizeDateForInput(value);
        if (!normalized) return '';

        const [year, month, day] = normalized.split('-');
        return `${month}/${day}/${year}`;
    };

    const birthDateValue = normalizeDateForInput(formData?.birthDate);
    const createdDateValue = normalizeDateForInput(createdDate || formData?.createdAt);
    const createdDateDisplay = formatDateForUsDisplay(createdDate || formData?.createdAt);

    const normalizedUserRole = String(userRole || '').trim().toLowerCase();
    const shouldHideCompanyFields = hideCompanyFields || normalizedUserRole === 'student';
        
    const [isEditing, setIsEditing] = useState(!isEditPage); // If adding, fields start enabled
    const [tempFormData, setTempFormData] = useState({ ...formData }); // Store initial values
    const [localErrors, setLocalErrors] = useState({});
    const [localEmail, setLocalEmail] = useState(formData.email || '')
    const [localEmailErrors, setLocalEmailErrors] = useState({});
    const [showCopied, setShowCopied] = useState(false);
    const copyIconRef = useRef(null);
    const copyTimeoutRef = useRef(null);
    const [isHovered, setIsHovered] = useState(false);

    const handleBirthDateChange = (e) => {
        const { value } = e.target;

        // Live validation
        if (!value.trim()) {
            setLocalErrors((prev) => ({ ...prev, birthDate: 'Birth Date is required.' }));
        } else {
            setLocalErrors((prev) => ({ ...prev, birthDate: '' }));
        }

        handleChange({ target: { name: 'birthDate', value } });
    };

    const handleEmailChange = (e) => {
        const { value } = e.target;

        // ✅ Update formData.email immediately so field is typeable
        handleChange({ target: { name: 'email', value } });

        // ✅ Debounce only the validation
        clearTimeout(window.emailValidationTimeout);
        window.emailValidationTimeout = setTimeout(() => {
            if (value.trim() === '') {
                setLocalEmailErrors((prev) => ({ ...prev, email: '' }));
            } else {
                validateField(
                    'email',
                    value.trim(),
                    validateEmail,
                    setLocalEmailErrors,
                    'Please enter a valid email address.'
                );
            }
        }, 800);
    };

    // Enable editing mode
    const handleEditClick = () => {
        setIsEditing(true);
        setTempFormData({ ...formData }); // Store previous values
    };

    // Save changes and disable editing
    const handleSaveClick = async (e) => {
        e.preventDefault();
        validateField('email', localEmail.trim(), validateEmail, setLocalEmailErrors, 'Please enter a valid email address.');

        if (typeof handleAboutSubmit === 'function') {
            const success = await handleAboutSubmit(e); // ✅ wait for result
            if (success) {
                setIsEditing(false); // ✅ Only if no validation/API errors
            }
        } else {
            console.error('Error: handleAboutSubmit is not a function.');
        }
    };

    // Cancel editing and restore previous values
    const handleCancelClick = () => {
        setIsEditing(false); // Exit edit mode

        if (typeof handleReset === 'function') {
            handleReset(tempFormData); // ✅ Restore saved values + clear errors
        }
    };

    const handleCopy = async (text) => {
        if (!text) return;

        try {
            await navigator.clipboard.writeText(text);

            setShowCopied(false);

            if (copyTimeoutRef.current) {
                clearTimeout(copyTimeoutRef.current);
            }

            requestAnimationFrame(() => {
                setShowCopied(true);

                copyTimeoutRef.current = setTimeout(() => {
                    setShowCopied(false);
                    copyTimeoutRef.current = null;
                }, 1200);
            });
        } catch (err) {
            console.error('Copy failed:', err);
        }
    };

    return (
        <>
        <Card className="mb-4 profile-section-card">
            <Card.Body>
                    {/* Title and Edit Button */}
                    <Row>
                        <Col>
                            <div className="profile-section-heading-row">
                            {isEditPage && !hideEdit && (
                                <div className="float-end d-none d-lg-block">
                                    {!isEditing ? (
                                        <Button variant="outline-secondary" className="btn-sm" onClick={handleEditClick}>Edit</Button>
                                    ) : (
                                        <>
                                            <Button variant="outline-secondary" className="btn-sm me-2" onClick={handleCancelClick}>Cancel</Button>
                                            <Button variant="primary" className="btn-sm" onClick={handleSaveClick}>Save</Button>
                                        </>
                                    )}
                                </div>
                            )}
                            <h3 className="company-detail-section-title d-lg-none"><span className="item-number">1.</span> About & contact info</h3>
                            <h3 className="d-none d-lg-flex">1. About & contact info</h3>
                            </div>
                            <hr className="d-lg-none" />
                        </Col>
                    </Row>
                    {isEditing ? (
                        <Row>
                            {/* Form Fields */}
                            <Col md={12} className="profile-edit-fields">
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group className="validated">
                                            <Form.Label>Date of birth <span>*</span></Form.Label>
                                            <div
                                                onClick={() => isEditing && document.getElementById('birthDate')?.showPicker?.()}
                                                style={{ position: 'relative', cursor: isEditing ? 'pointer' : 'not-allowed' }}
                                            >
                                                <Form.Control
                                                    id="birthDate"
                                                    type="date"
                                                    name="birthDate"
                                                    value={birthDateValue}
                                                    onChange={handleBirthDateChange}
                                                    isInvalid={!!errors.birthDate || !!localErrors.birthDate}
                                                    required
                                                    disabled={!isEditing}
                                                    className="custom-date-input"
                                                />

                                                {(errors.birthDate || localErrors.birthDate) && (
                                                    <div className="custom-error-icon-wrapper">
                                                        <img
                                                            src={icValidationError}
                                                            alt={errors.birthDate || localErrors.birthDate}
                                                            className="custom-error-icon"
                                                        />
                                                        <div className="custom-tooltip">
                                                            {errors.birthDate || localErrors.birthDate}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group controlId="dateJoined">
                                            <Form.Label>Date joined</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="dateJoined"
                                                disabled
                                                value={createdDateDisplay}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group controlId="phone">
                                            <Form.Label>Phone</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                isInvalid={!!errors.phone}
                                                required
                                                disabled={!isEditing}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.phone}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="validated" controlId="email">
                                            <Form.Label>Email <span>*</span></Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleEmailChange}
                                                isInvalid={!!errors.email || !!localErrors.email}
                                                required
                                                disabled={disableEmail || !isEditing}
                                            />
                                            {(errors.email || localErrors.email) && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img
                                                        src={icValidationError}
                                                        alt={errors.email || localErrors.email}
                                                        className="custom-error-icon"
                                                    />
                                                    <div className="custom-tooltip">
                                                        {errors.email || localErrors.email}
                                                    </div>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>
                                {!shouldHideCompanyFields && (
                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <Form.Group controlId="company">
                                                <Form.Label>Company</Form.Label>
                                                <Form.Select
                                                    name="company"
                                                    value={normalizeId(formData?.company)}
                                                    onChange={(e) =>
                                                        handleChange({
                                                            target: { name: 'company', value: e.target.value },
                                                        })
                                                    }
                                                    disabled={!isEditing}
                                                >
                                                    <option value="">Select company</option>

                                                    {(Array.isArray(companies) ? companies : []).map((company) => {
                                                        const optId = normalizeId(company._id ?? company.id ?? company);
                                                        return (
                                                            <option key={optId} value={optId}>
                                                                {company.title ?? company.name ?? optId}
                                                            </option>
                                                        );
                                                    })}

                                                    {(() => {
                                                        const selectedCompanyId = normalizeId(formData?.company);
                                                        const inList = (Array.isArray(companies) ? companies : []).some((c) => {
                                                            const cid = normalizeId(c._id ?? c.id ?? c);
                                                            return cid === selectedCompanyId;
                                                        });
                                                        const savedLabel =
                                                            formData?.company?.title ??
                                                            (Array.isArray(companies)
                                                                ? (companies.find((c) => normalizeId(c._id ?? c.id ?? c) === selectedCompanyId)?.title)
                                                                : undefined) ??
                                                            'Unknown company';

                                                        return selectedCompanyId && !inList ? (
                                                            <option value={selectedCompanyId}>(saved) {savedLabel}</option>
                                                        ) : null;
                                                    })()}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group controlId="companyRole" className="validated">
                                                <Form.Label>Company Role</Form.Label>
                                                <Form.Select
                                                    name="companyRole"
                                                    value={formData.companyRole || ''}
                                                    onChange={handleChange}
                                                    isInvalid={!!errors.companyRole}
                                                    disabled={!isEditing}
                                                >
                                                    <option value="">Please choose</option>
                                                    <option value="Admin">Admin</option>
                                                    <option value="User">User</option>
                                                </Form.Select>
                                                {errors.companyRole && (
                                                    <div className="custom-error-icon-wrapper">
                                                        <img
                                                            src={icValidationError}
                                                            alt={errors.companyRole}
                                                            className="custom-error-icon"
                                                        />
                                                        <div className="custom-tooltip">
                                                            {errors.companyRole}
                                                        </div>
                                                    </div>
                                                )}
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                )}
                            </Col>
                        </Row>
                    ) : (
                        <Row>  
                            <Col md={12}>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <div className="icon-text">
                                            <span className="icon" aria-hidden="true">
                                                <Image src={icCalendar} alt="Date of birth" />
                                            </span>
                                            <span className="text">
                                                Date of birth:
                                                <br />
                                                <strong>
                                                    {formatDateForDisplay(formData.birthDate)}
                                                </strong>
                                            </span>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="icon-text">
                                            <span className="icon" aria-hidden="true">
                                                <Image src={icCalendar} alt="Date Joined" />
                                            </span>
                                            <span className="text">
                                                Date Joined:
                                                <br />
                                                <strong>
                                                    {createdDateDisplay || "N/A"}
                                                </strong>
                                            </span>
                                        </div>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <div className="icon-text">
                                            <span className="icon" aria-hidden="true">
                                                <Image src={icPhone} alt="Phone" />
                                            </span>
                                            <span className="text">
                                                Phone:
                                                <br />
                                                <strong>
                                                    {formData.phone || "N/A"}
                                                </strong>
                                            </span>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="icon-text">
                                            <span className="icon" aria-hidden="true">
                                                <Image src={icMail} alt="Email" />
                                            </span>

                                            <span className="text w-100">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <span>Email:</span>

                                                    {formData.email && !disableEmail && (
                                                        <span
                                                            ref={copyIconRef}
                                                            className="d-inline-flex align-items-center flex-shrink-0 cursor-pointer"
                                                            onClick={() => handleCopy(formData.email)}
                                                            onMouseEnter={() => setIsHovered(true)}
                                                            onMouseLeave={() => setIsHovered(false)}
                                                        >
                                                            <Image src={icCopy} alt="Copy Email Address" />
                                                        </span>
                                                    )}
                                                </div>

                                                <strong
                                                    className={`truncate-email d-block${disableEmail ? ' text-muted' : ' cursor-pointer'}`}
                                                    onClick={() => !disableEmail && formData.email && handleCopy(formData.email)}
                                                    onMouseEnter={() => !disableEmail && setIsHovered(true)}
                                                    onMouseLeave={() => !disableEmail && setIsHovered(false)}
                                                >
                                                    {formData.email || 'N/A'}
                                                </strong>

                                                {!disableEmail && (
                                                    <Overlay
                                                        target={copyIconRef.current}
                                                        show={!!formData.email && (showCopied || isHovered)}
                                                        placement="top"
                                                        container={document.body}
                                                        popperConfig={{ strategy: 'fixed' }}
                                                    >
                                                        {(props) => (
                                                            <Tooltip id="copy-email-tooltip" {...props}>
                                                                {showCopied ? 'Email address copied' : 'Copy email'}
                                                            </Tooltip>
                                                        )}
                                                    </Overlay>
                                                )}
                                            </span>
                                        </div>
                                    </Col>
                                </Row>
                                {!shouldHideCompanyFields && (
                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <div className="icon-text">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icCompany} alt="Company" />
                                                </span>
                                                <span className="text">
                                                    Company:
                                                    <br />
                                                    <strong>
                                                        {companyTitle || companyId || "N/A"}
                                                    </strong>
                                                </span>
                                            </div>
                                        </Col>
                                    </Row>
                                )}
                            </Col>
                        </Row>
                    )}
                    <ProfileMobileSectionActions
                        isEditing={isEditing}
                        onEdit={handleEditClick}
                        onCancel={handleCancelClick}
                        onSave={handleSaveClick}
                        show={isEditPage && !hideEdit}
                    />
                </Card.Body>
            </Card>
        </>
    );
};

export default UserAddAbout;
